import React, { useState, Suspense } from 'react';
import { Loader, Search, X, QrCode } from 'lucide-react';
import { useQueryState, parseAsString } from 'nuqs';
import { useDebounce } from '../hooks/useDebounce';
import { useVehicleSearch } from '../hooks/useVehicleSearch';
import VehicleItem from '../pages/vehicle/VehicleItem';
const QRScanner = React.lazy(() => import('./QRScanner'));

const VehicleSearch = ({
  setEditingVehicle,
  setReconRequestVehicle,
  setViewingReconRequests,
}) => {
  const [searchTerm, setSearchTerm] = useQueryState(
    'vin',
    parseAsString.withDefault('').withOptions({ throttleMs: 300 })
  );
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState('available');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { data, isLoading, error } = useVehicleSearch(debouncedSearchTerm, inventoryStatus);

  // data is already the array of vehicles from the hook
  const vehicles = Array.isArray(data) ? data : [];

  const handleQRScanSuccess = (vin) => {
    console.log('QR Code scanned successfully:', vin);
    setSearchTerm(vin);
    setShowQRScanner(false);
  };

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col bg-gray-100">
      <div className="container mx-auto flex-1 flex flex-col md:px-6 px-3">
        <div className="w-full flex flex-col items-center justify-center py-5 sticky top-[4.5rem] bg-gray-100 z-10 gap-3">
          <div className="relative lg:w-1/3 md:w-1/2 w-full flex items-center gap-2">
            <div className="relative flex-1 flex items-center">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by VIN (e.g., 1HGBH41JXMN109186)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none bg-white text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Search vehicles by VIN"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* QR Scanner Button */}
            <button
              onClick={() => setShowQRScanner(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm hover:shadow-md"
              title="Scan QR Code"
              aria-label="Open QR code scanner"
            >
              <QrCode size={20} />
              <span className="hidden md:inline text-sm font-medium">Scan</span>
            </button>

          </div>
          
          {/* Inventory Status Toggle */}
          <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-300">
            <span className="text-sm font-medium text-gray-700">Show:</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setInventoryStatus('available')}
                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${
                  inventoryStatus === 'available'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Available
              </button>
              <button
                onClick={() => setInventoryStatus('sold')}
                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${
                  inventoryStatus === 'sold'
                    ? 'bg-red-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Sold
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex justify-center">
          {!searchTerm.trim() ? (
            <div className="text-center px-4 my-auto">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-purple-500 via-red-500 via-orange-500 to-yellow-400 bg-clip-text text-transparent mb-2">
                No vehicle selected yet
              </h2>
              <p className="text-gray-500">
                Enter a VIN number to search for vehicle inventory.
              </p>
            </div>
          ) : isLoading && debouncedSearchTerm.trim() ? (
            <div className="text-center py-4 my-auto">
              <Loader
                size={50}
                className="animate-spin text-orange-500 mx-auto"
              />
              <p className="text-gray-500 text-center">Loading vehicles...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="mb-4">
                <Search
                  size={48}
                  className="mx-auto text-gray-400"
                />
              </div>
              <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-500 via-red-500 via-orange-500 to-yellow-400 bg-clip-text text-transparent mb-2">
                Vehicle not found
              </h3>
              <p className="text-gray-500">
                No vehicle found with this VIN
              </p>
            </div>
          ) : debouncedSearchTerm.trim() && vehicles.length === 0 ? (
            <div className="text-center py-4">
              <div className="mb-4">
                <Search
                  size={48}
                  className="mx-auto text-transparent bg-gradient-to-r from-purple-500 via-red-500 via-orange-500 to-yellow-400 bg-clip-text"
                />
              </div>
              <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-500 via-red-500 via-orange-500 to-yellow-400 bg-clip-text text-transparent mb-2">
                No vehicles found
              </h3>
              <p className="text-gray-500">Try adjusting your search term</p>
            </div>
          ) : vehicles.length > 0 ? (
            <div className="w-full max-w-7xl">
              {vehicles.length > 1 && (
                <div className="mb-4 text-center">
                  <p className="text-sm text-gray-600">
                    Found{' '}
                    <span className="font-semibold text-orange-600">
                      {vehicles.length}
                    </span>{' '}
                    vehicle{vehicles.length > 1 ? 's' : ''} matching your search
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => (
                  <VehicleItem
                    key={vehicle.vehicle_inventory_id}
                    vehicle={vehicle}
                    setEditingVehicle={setEditingVehicle}
                    setReconRequestVehicle={setReconRequestVehicle}
                    setViewingReconRequests={setViewingReconRequests}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center px-4 my-auto">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-purple-500 via-red-500 via-orange-500 to-yellow-400 bg-clip-text text-transparent mb-2">
                No vehicle selected yet
              </h2>
              <p className="text-gray-500">
                Enter a VIN number to search for vehicle inventory.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* QR Scanner Modal */}
      {showQRScanner && (
        <Suspense fallback={<div className="h-64 w-full bg-muted animate-pulse rounded-lg" />}>
          <QRScanner
            onScanSuccess={handleQRScanSuccess}
            onClose={() => setShowQRScanner(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default VehicleSearch;
